
import { useState, useEffect } from 'react';

export interface CartItem {
  id: string;
  title: string;
  price: number;
  quantity: number;
  image: string;
  category: string;
  description?: string;
  location?: string;
  itinerary?: Array<{
    day: number;
    title: string;
    description: string;
  }>;
}

export const useCart = () => {
  const [cartItems, setCartItems] = useState<CartItem[]>([]);

  // Load cart from localStorage on mount
  useEffect(() => {
    const savedCart = localStorage.getItem('travelCart');
    if (savedCart) {
      try {
        setCartItems(JSON.parse(savedCart));
      } catch (error) {
        console.error('Error loading cart from localStorage:', error);
      }
    }
  }, []);

  // Save cart to localStorage whenever it changes
  useEffect(() => {
    localStorage.setItem('travelCart', JSON.stringify(cartItems));
  }, [cartItems]);

  // Listen for cart updates dispatched elsewhere in the app and sync
  useEffect(() => {
    const handler = (e: Event) => {
      const custom = e as CustomEvent<CartItem[]>;
      if (custom?.detail) {
        setCartItems(custom.detail);
      }
    };

    window.addEventListener('cart-updated', handler as EventListener);
    return () => window.removeEventListener('cart-updated', handler as EventListener);
  }, []);

  const addToCart = (item: Omit<CartItem, 'quantity'>): CartItem[] => {
    // Read latest cart from localStorage to avoid overwriting changes from other instances
    let prevItems: CartItem[] = [];
    try {
      const saved = localStorage.getItem('travelCart');
      prevItems = saved ? JSON.parse(saved) : cartItems;
    } catch (err) {
      prevItems = cartItems;
    }

    const existingItem = prevItems.find(cartItem => cartItem.id === item.id);

    const newItems = existingItem
      ? prevItems.map(cartItem =>
        cartItem.id === item.id
          ? { ...cartItem, quantity: cartItem.quantity + 1 }
          : cartItem
      )
      : [...prevItems, { ...item, quantity: 1 }];

    try {
      localStorage.setItem('travelCart', JSON.stringify(newItems));
    } catch (err) { }

    // update local state first, then notify other listeners
    setCartItems(newItems);
    try {
      window.dispatchEvent(new CustomEvent('cart-updated', { detail: newItems }));
    } catch (err) { }
    return newItems;
  };

  const removeFromCart = (id: string) => {
    // Read latest cart from localStorage
    let prevItems: CartItem[] = [];
    try {
      const saved = localStorage.getItem('travelCart');
      prevItems = saved ? JSON.parse(saved) : cartItems;
    } catch (err) {
      prevItems = cartItems;
    }

    const newItems = prevItems.filter(item => item.id !== id);
    try {
      localStorage.setItem('travelCart', JSON.stringify(newItems));
    } catch (err) { }
    setCartItems(newItems);
    try {
      window.dispatchEvent(new CustomEvent('cart-updated', { detail: newItems }));
    } catch (err) { }
  };

  const updateQuantity = (id: string, quantity: number) => {
    if (quantity <= 0) {
      removeFromCart(id);
      return;
    }

    // Read latest cart from localStorage
    let prevItems: CartItem[] = [];
    try {
      const saved = localStorage.getItem('travelCart');
      prevItems = saved ? JSON.parse(saved) : cartItems;
    } catch (err) {
      prevItems = cartItems;
    }

    const newItems = prevItems.map(item => (item.id === id ? { ...item, quantity } : item));
    try {
      localStorage.setItem('travelCart', JSON.stringify(newItems));
    } catch (err) { }
    setCartItems(newItems);
    try {
      window.dispatchEvent(new CustomEvent('cart-updated', { detail: newItems }));
    } catch (err) { }
  };

  const clearCart = () => {
    const newItems: CartItem[] = [];
    try {
      localStorage.setItem('travelCart', JSON.stringify(newItems));
    } catch (err) { }
    setCartItems(newItems);
    try {
      window.dispatchEvent(new CustomEvent('cart-updated', { detail: newItems }));
    } catch (err) { }
  };

  const getCartTotal = () => {
    return cartItems.reduce((total, item) => total + (item.price * item.quantity), 0);
  };

  const getCartItemsCount = () => {
    return cartItems.reduce((count, item) => count + item.quantity, 0);
  };

  return {
    cartItems,
    addToCart,
    removeFromCart,
    updateQuantity,
    clearCart,
    getCartTotal,
    getCartItemsCount
  };
};
